import {
  BankOutlined,
  DatabaseOutlined,
  DropboxOutlined,
  EnvironmentOutlined,
  ExperimentOutlined,
  HomeOutlined,
  LayoutOutlined,
  TableOutlined,
} from '@ant-design/icons'
import { Horizontal, Vertical } from '@gravitate-js/excalibrr'
import { Alert, Radio, Spin, Tabs } from 'antd'
import React, { useState, useMemo, useEffect } from 'react'
import { useCrossroadsEntityTypes } from '../../api/hooks/useCrossroadsEntityTypes'
import { ErrorBoundary } from '../../components/ErrorBoundary'
import { MasterDataGrid } from './MasterDataGrid/MasterDataGrid'
import { ThreeWayMappingView } from './ThreeWayMappingView/ThreeWayMappingView'

const MASTER_DATA_TAB_STORAGE_KEY = 'rita-master-data-active-tab'

interface FilterInfo {
  searchId?: string
  entityType?: string
}

// Icon mapping for entity types (using singular keys)
const iconMap: Record<string, React.ReactNode> = {
  company: <BankOutlined />,
  counterparty: <BankOutlined />,
  location: <EnvironmentOutlined />,
  product: <ExperimentOutlined />,
  terminal: <HomeOutlined />,
  site: <HomeOutlined />,
  tank: <DropboxOutlined />,
}

export const MasterDataManagement = () => {
  const [activeTab, setActiveTabState] = useState(() => {
    const stored = localStorage.getItem(MASTER_DATA_TAB_STORAGE_KEY)
    return stored || 'product'
  })

  const [viewMode, setViewMode] = useState<'single' | '3way'>(() => {
    const stored = localStorage.getItem('master-data-view-mode')
    return (stored as 'single' | '3way') || 'single'
  })

  const [filterInfo, setFilterInfo] = useState<FilterInfo | null>(null)

  const setActiveTab = (tab: string) => {
    setActiveTabState(tab)
    localStorage.setItem(MASTER_DATA_TAB_STORAGE_KEY, tab)
  }

  const handleViewModeChange = (mode: 'single' | '3way') => {
    setViewMode(mode)
    localStorage.setItem('master-data-view-mode', mode)
  }

  // Check for filter info from Connection Logs
  useEffect(() => {
    const filterData = sessionStorage.getItem('referenceDataFilter')
    if (filterData) {
      try {
        const parsed = JSON.parse(filterData)
        setFilterInfo(parsed)
        // Set the active tab to the entity type
        if (parsed.entityType) {
          setActiveTab(parsed.entityType)
        }
        // Clear the session storage after reading
        sessionStorage.removeItem('referenceDataFilter')
      } catch (error) {
        console.error('Failed to parse filter data:', error)
      }
    }
  }, [])

  const handleClearFilter = () => {
    setFilterInfo(null)
  }

  // Fetch entity types dynamically
  const { data: entityTypes, isLoading } = useCrossroadsEntityTypes()

  // Build tab items from entity types
  const tabItems = useMemo(() => {
    if (!entityTypes || entityTypes.length === 0) {
      return []
    }

    return entityTypes.map((entityType) => ({
      key: entityType.key,
      label: (
        <Horizontal verticalCenter gap={8}>
          {iconMap[entityType.key] || <DatabaseOutlined />}
          <span>{entityType.label || entityType.key}</span>
        </Horizontal>
      ),
    }))
  }, [entityTypes])

  // Make sure active tab is valid
  const validActiveTab = tabItems.some((item) => item.key === activeTab)
    ? activeTab
    : tabItems[0]?.key || ''

  return (
    <ErrorBoundary>
      <Vertical className='h-full'>
        {/* Alert Banner when in filter mode */}
        {filterInfo && (
          <Alert
            message={`Filtering Reference Data: Looking for ${filterInfo.searchId} to resolve ${filterInfo.issue}`}
            description={`The grid below is filtered to help you find and map the item. Close this alert to clear the filter.`}
            type='warning'
            closable
            onClose={handleClearFilter}
            style={{ margin: '8px 16px' }}
            showIcon
          />
        )}
        <Tabs
          activeKey={validActiveTab}
          onChange={setActiveTab}
          className='px-3 rounded-sm border border-[var(--gray-300)]'
          style={{
            background: 'var(--bg-2)',
            flex: 1,
            marginBottom: 0,
          }}
          tabBarStyle={{
            marginBottom: 0,
            border: 'none',
          }}
          items={tabItems}
          tabBarExtraContent={
            <>
              <Radio.Group
                value={viewMode}
                onChange={(e) => handleViewModeChange(e.target.value)}
                buttonStyle='solid'
                size='small'
              >
                <Radio.Button value='single'>
                  <TableOutlined /> Master Data
                </Radio.Button>
                <Radio.Button value='3way'>
                  <LayoutOutlined /> 3-Way View
                </Radio.Button>
              </Radio.Group>
            </>
          }
        />

        <Vertical>
          {isLoading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Spin size='large' />
            </div>
          ) : !validActiveTab ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <div>No entity types available</div>
            </div>
          ) : viewMode === 'single' ? (
            <MasterDataGrid
              entityType={validActiveTab}
              filterInfo={filterInfo}
            />
          ) : (
            <ThreeWayMappingView entityType={validActiveTab} />
          )}
        </Vertical>
      </Vertical>
    </ErrorBoundary>
  )
}
